import { NetworkStatus, useQuery } from '@apollo/client';
import {
  Box,
  Divider,
  FormControl,
  FormLabel,
  Spacer,
  Stack,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { Pagination } from '@/components/Pagination';
import { Search } from '@/components/Search';
import {
  AdminEventsInput_Filter_HostedIn,
  DateWindowType,
  ERegistrationType,
  EventStatus,
  EventVenueType,
  SortOptions,
} from '@/graphql-types/globalTypes';
import { Card } from '@/imports/ui/chakra/components/Card';
import { useDebounce } from '@/imports/ui/hooks/useDebounce';

import { ADMIN_EVENTS_TABLE_QUERY } from '../../graphql/adminEventsTableQuery';
import {
  AllEvents_AdminEvents,
  AllEvents_AdminEventsVariables,
} from '../../graphql/graphql-types/AllEvents_AdminEvents';

import { EventsTable } from '../EventsTable';
import { HostedInMenu } from './components/HostedInMenu';
import { RegistrationTypeMenu } from './components/RegistrationTypeMenu';
import { EVENT_SITUATION, SituationMenu } from './components/SituationMenu';
import { VenueTypeMenu } from './components/VenueTypeMenu';

const LIMIT = 10;

const getAdminEventsPageCount = ({
  currentPage,
  totalCount,
  hasMore,
}: {
  currentPage: number;
  totalCount: number;
  hasMore: boolean;
}): number => {
  const pagesFromTotal =
    totalCount > LIMIT ? Math.ceil(totalCount / LIMIT) : 0;

  if (hasMore) {
    return Math.max(currentPage + 1, pagesFromTotal);
  }

  return Math.max(currentPage, pagesFromTotal);
};

export const AllEvents: React.FC = () => {
  const now = React.useRef(new Date());
  const router = useRouter();

  React.useEffect(() => {
    if (!router.isReady) {
      return;
    }
    if (router.query.eventType == null) {
      return;
    }
    const { eventType: _omit, ...rest } = router.query;
    void router.replace({ pathname: router.pathname, query: rest }, undefined, {
      shallow: true,
    });
  }, [router, router.isReady, router.query.eventType]);

  const searchText = router.query.searchText
    ? router.query.searchText.toString()
    : undefined;

  const hostedIn = router.query.hostedIn
    ? (router.query.hostedIn as AdminEventsInput_Filter_HostedIn)
    : undefined;

  const registrationType = router.query.registrationType
    ? (router.query.registrationType as ERegistrationType)
    : undefined;

  const offset = router.query.offset ? Number(router.query.offset) : 0;

  const eventSituation = router.query.eventSituation
    ? (router.query.eventSituation as EVENT_SITUATION)
    : EVENT_SITUATION.LIVE_AND_UPCOMING;

  const venueType = router.query.venueType
    ? (router.query.venueType as EventVenueType)
    : undefined;

  const query = useQuery<AllEvents_AdminEvents, AllEvents_AdminEventsVariables>(
    ADMIN_EVENTS_TABLE_QUERY,
    {
      variables: {
        input: {
          limit: LIMIT,
          offset: LIMIT * offset,
          filter: {
            // Omit `eventType` so the API returns events only (not Learning courses).
            searchText: searchText ? searchText : undefined,
            hostedIn: hostedIn ? [hostedIn] : undefined,
            registrationType: registrationType ? [registrationType] : undefined,
            venueType: venueType ? [venueType] : undefined,
            eventStatus: {
              [EVENT_SITUATION.LIVE_AND_UPCOMING]: [EventStatus.ACTIVE],
              [EVENT_SITUATION.DRAFT]: [EventStatus.DRAFT],
              [EVENT_SITUATION.PAST]: [EventStatus.ACTIVE],
              [EVENT_SITUATION.INACTIVE]: [EventStatus.INACTIVE],
              [EVENT_SITUATION.ALL]: undefined,
            }[eventSituation],
            dateWindow: {
              [EVENT_SITUATION.LIVE_AND_UPCOMING]: {
                windowType: DateWindowType.AnyOverlap,
                windowStart: now.current.toISOString(),
              },
              [EVENT_SITUATION.DRAFT]: undefined,
              [EVENT_SITUATION.PAST]: {
                windowType: DateWindowType.EndsWithin,
                windowEnd: now.current.toISOString(),
              },
              [EVENT_SITUATION.INACTIVE]: undefined,
              [EVENT_SITUATION.ALL]: undefined,
            }[eventSituation],
          },
          sortOptions: {
            startDate: {
              [EVENT_SITUATION.LIVE_AND_UPCOMING]: SortOptions.asc,
              [EVENT_SITUATION.DRAFT]: SortOptions.asc,
              [EVENT_SITUATION.PAST]: SortOptions.desc,
              [EVENT_SITUATION.INACTIVE]: SortOptions.desc,
              [EVENT_SITUATION.ALL]: SortOptions.desc,
            }[eventSituation],
          },
        },
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const setSearchText = useDebounce(
    (next: string) =>
      router.replace({
        pathname: router.pathname,
        query: { ...router.query, searchText: next, offset: 0 },
      }),
    500
  );

  const hasFilter =
    searchText ||
    hostedIn ||
    registrationType ||
    eventSituation ||
    venueType;

  return (
    <Stack spacing="6">
      <Card as={Stack} spacing="0">
        <Box px="4">
          <Stack direction="row" alignItems="center">
            <SituationMenu
              value={eventSituation}
              onChange={(nextSituation) => {
                return router.replace({
                  pathname: router.pathname,
                  query: {
                    ...router.query,
                    eventSituation: nextSituation,
                    offset: 0,
                  },
                });
              }}
            />
            <Spacer />
            <Search
              defaultValue={searchText}
              onChange={setSearchText}
              isLoading={query.networkStatus === NetworkStatus.setVariables}
              label="Search events"
              maxW="xs"
            />
          </Stack>
          <Divider />
        </Box>

        <Stack direction="row" spacing="4" justifyContent="flex-start" p="4">
          <FormControl maxW="3xs">
            <FormLabel>Location</FormLabel>
            <VenueTypeMenu
              value={venueType}
              onChange={(next) =>
                router.replace({
                  pathname: router.pathname,
                  query: { ...router.query, venueType: next, offset: 0 },
                })
              }
            />
          </FormControl>

          <FormControl maxW="3xs">
            <FormLabel>Registration</FormLabel>
            <RegistrationTypeMenu
              value={registrationType}
              onChange={(next) =>
                router.replace({
                  pathname: router.pathname,
                  query: { ...router.query, registrationType: next, offset: 0 },
                })
              }
            />
          </FormControl>

          <FormControl maxW="3xs">
            <FormLabel>Host</FormLabel>
            <HostedInMenu
              value={hostedIn}
              onChange={(next) =>
                router.replace({
                  pathname: router.pathname,
                  query: { ...router.query, hostedIn: next, offset: 0 },
                })
              }
            />
          </FormControl>
        </Stack>
      </Card>

      <EventsTable
        events={query.data?.adminEvents.results}
        isLoading={!query.data?.adminEvents && query.loading}
        hasFilter={!!hasFilter}
      />

      <Pagination
        currentPage={offset + 1}
        numberOfPages={
          query.data?.adminEvents
            ? getAdminEventsPageCount({
                currentPage: offset + 1,
                totalCount: query.data.adminEvents.totalCount,
                hasMore: query.data.adminEvents.hasMore,
              })
            : 0
        }
        onChange={(value) =>
          router.replace({
            pathname: router.pathname,
            query: { ...router.query, offset: value - 1 },
          })
        }
      />
    </Stack>
  );
};
